import React, { useCallback, useEffect, useMemo, useState } from "react";
import { message } from "antd";
import dayjs from "dayjs";
import { Archive, Download, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import ConfirmationModal from "src/components/reusable/ConfirmationModal";
import CampaignTable from "src/components/reusable/CampaignTable/CampaignTable";
import ExportConfirmationModal from "src/components/reusable/ExportConfirmationModal/ExportConfirmationModal";
import NoDataDisplay from "src/components/reusable/NoDataDisplay";
import StyledInput from "src/components/reusable/StyledInput";
import SelectMenu from "src/components/common/SelectMenu/SelectMenu";
import {
  DashboardNarrativePanel,
  DefinitionTooltip,
  TablePagePagination,
} from "src/components/common";
import { useDashboardNarrative } from "src/hooks/UseDashboardNarrative";
import { useAsyncResource } from "src/hooks/UseAsyncResource";
import { useLanguage } from "src/hooks/UseLanguage";
import {
  engagementOverviewService,
  type EngagementAchievementsData,
} from "src/services/EngagementOverviewAPI";
import {
  getCustomLists,
  getAudienceSummary,
  getCustomerGroups,
  deleteCustomList,
  updateCustomListStatus,
} from "src/services/AudienceListAPIs";
import {
  BusinessImpactSection,
  mapAchievements,
  readFormattedText,
  readNumericValue,
} from "src/layouts/Engagements/Overview/Index";
import { formatNumber } from "src/utils/numberFormat";
import { useDynamicTextTranslator } from "src/utils/dynamicTextTranslation";
import { getApiLangFromI18n } from "src/utils/apiLang";
import type {
  AudienceSummaryResponse,
  CustomList,
  CustomerGroupsResponse,
} from "../../EngagementInterfaces";
import { handleBulkActionConfirm } from "./Audiencehandlers";
import AudienceBarChart, { type PredictedGroupBar } from "./AudienceBarChart";
import AudienceCreateMenu from "./AudienceCreateMenu";
import AudienceListModal from "./AudienceListModal";
import { useAudienceColumns } from "./AudienceColumns";
import { useAudienceExport } from "./useAudienceExport";
import { groupNeedsAttention } from "./customerGroupUtils";
import {
  countListsByKind,
  getAudienceListKind,
  isArchivedList,
  isPredictedList,
  parseCreatedAt,
  type AudienceSortOrder,
} from "./audienceListUtils";
import "../../Overview/Overview.scss";
import "./AudienceOverview.scss";

const PAGE_SIZE = 8;
const ACHIEVEMENTS_LOOKBACK_MONTHS = 3;
const API_DATE_FORMAT = "YYYY-MM-DD";
const EMPTY_VALUE = "—";

type AudienceAction = "archive" | "unarchive" | "delete";

const ACTION_ICONS: Record<AudienceAction, string> = {
  delete: "/Icons/delete-icon.svg",
  archive: "/Icons/archive-icon.svg",
  unarchive: "/Icons/unarchive-icon.svg",
};

const AudienceScreen: React.FC = () => {
  const { t: i18nT, i18n } = useTranslation();
  useLanguage();
  const lang = getApiLangFromI18n(i18n);
  const t = useMemo(
    () =>
      new Proxy({} as Record<string, string>, {
        get: (_, prop: string) =>
          i18nT(`audienceOverview.${String(prop)}`, { lng: i18n.language }),
      }),
    [i18nT, i18n.language]
  );

  const [data, setData] = useState<CustomList[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [listsLoading, setListsLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [sortOrder, setSortOrder] = useState<AudienceSortOrder>("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLists, setSelectedLists] = useState<number[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [selectedListId, setSelectedListId] = useState<number | null>(null);
  const [actionType, setActionType] = useState<AudienceAction | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [openedListId, setOpenedListId] = useState<number | null>(null);

  const { startDate, endDate } = useMemo(
    () => ({
      startDate: dayjs().subtract(ACHIEVEMENTS_LOOKBACK_MONTHS, "month").format(API_DATE_FORMAT),
      endDate: dayjs().format(API_DATE_FORMAT),
    }),
    []
  );

  const formatLocalizedNumber = useCallback(
    (value: number) => formatNumber(value, {}, i18n.language),
    [i18n.language]
  );

  const { exportSingleList, exportCombinedLists, exportSeparateLists } = useAudienceExport(data);

  const fetchCustomLists = useCallback(async () => {
    setListsLoading(true);
    try {
      const response = await getCustomLists();
      setData(response.status === "success" ? response.custom_lists || [] : []);
    } catch (err) {
      console.error(i18nT("audienceOverview.errorFetchingCustomLists"), err);
      setData([]);
    } finally {
      setListsLoading(false);
      setInitialLoading(false);
    }
  }, [i18nT]);

  useEffect(() => {
    fetchCustomLists();
  }, [fetchCustomLists, lang]);

  const audienceSummaryRequest = useCallback(() => getAudienceSummary(lang), [lang]);

  const audienceSummary = useAsyncResource<AudienceSummaryResponse>(
    audienceSummaryRequest,
    t.audienceSummaryLoadFailed
  );

  const customerGroupsRequest = useCallback(() => getCustomerGroups(lang), [lang]);

  const customerGroups = useAsyncResource<CustomerGroupsResponse>(
    customerGroupsRequest,
    t.customerGroupsLoadFailed
  );

  const summary = audienceSummary.data?.data ?? null;
  const totalMembers = readNumericValue(summary?.total_members);

  const achievementsRequest = useCallback(
    () =>
      engagementOverviewService.fetchAchievements({
        start_date: startDate,
        end_date: endDate,
        campaign_name: null,
      }),
    [startDate, endDate]
  );

  const achievements = useAsyncResource<EngagementAchievementsData>(
    achievementsRequest,
    t.achievementsLoadFailed
  );

  const businessImpact = useMemo(
    () => mapAchievements(achievements.data),
    [achievements.data]
  );

  const groupItems = useMemo(() => customerGroups.data?.data ?? [], [customerGroups.data]);

  const dynamicTextPool = useMemo(
    () => [
      ...data.flatMap(list => [list?.list_name, list?.description, ...(list?.tags || [])]),
      ...groupItems.map(group => group.name),
    ],
    [data, groupItems]
  );

  const translateDynamicLabel = useDynamicTextTranslator(dynamicTextPool, i18n.language);

  const activeLists = useMemo(() => data.filter(list => !isArchivedList(list)), [data]);

  const openedList = useMemo(
    () => data.find(list => list.custom_list_id === openedListId) ?? null,
    [data, openedListId]
  );

  const scopedLists = useMemo(
    () => data.filter(list => isArchivedList(list) === showArchived),
    [data, showArchived]
  );

  const kindCounts = useMemo(() => countListsByKind(activeLists), [activeLists]);

  const typeOptions = useMemo(
    () => [
      { value: "", label: t.typeFilter },
      { value: "predicted", label: t.typePredicted },
      { value: "uploaded", label: t.typeUploaded },
      { value: "segment", label: t.typeSegment },
    ],
    [t]
  );

  const tagOptions = useMemo(
    () => [
      { value: "", label: t.tagsFilter },
      ...Array.from(new Set(scopedLists.flatMap(list => list.tags ?? []))).map(tag => ({
        value: tag,
        label: translateDynamicLabel(tag) || tag,
      })),
    ],
    [scopedLists, t, translateDynamicLabel]
  );

  const visibleLists = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const matchesQuery = (value: string | null | undefined) =>
      Boolean(value) &&
      String(translateDynamicLabel(value) || value).toLowerCase().includes(query);

    return scopedLists
      .filter(list => {
        if (typeFilter && getAudienceListKind(list.list_type) !== typeFilter) {
          return false;
        }
        if (tagFilter && !(list.tags ?? []).includes(tagFilter)) {
          return false;
        }
        return (
          !query ||
          [list.list_name, list.description, ...(list.tags ?? [])].some(matchesQuery)
        );
      })
      .sort((first, second) => {
        const difference =
          (parseCreatedAt(first.created_at)?.valueOf() ?? 0) -
          (parseCreatedAt(second.created_at)?.valueOf() ?? 0);
        return sortOrder === "newest" ? -difference : difference;
      });
  }, [scopedLists, searchQuery, typeFilter, tagFilter, sortOrder, translateDynamicLabel]);

  const totalPages = Math.max(1, Math.ceil(visibleLists.length / PAGE_SIZE));

  const paginatedLists = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return visibleLists.slice(start, start + PAGE_SIZE);
  }, [visibleLists, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, typeFilter, tagFilter, sortOrder, showArchived]);

  useEffect(() => {
    setCurrentPage(page => Math.min(page, totalPages));
  }, [totalPages]);

  const visibleIds = useMemo(
    () => visibleLists.map(list => list.custom_list_id),
    [visibleLists]
  );

  const allSelected =
    visibleIds.length > 0 && visibleIds.every(id => selectedLists.includes(id));

  const hasPredictedSelection = useMemo(
    () =>
      data.some(list => selectedLists.includes(list.custom_list_id) && isPredictedList(list)),
    [data, selectedLists]
  );

  const predictedGroups = useMemo<PredictedGroupBar[]>(
    () =>
      groupItems.map(group => {
        const members = readNumericValue(group.count) ?? 0;
        const changePercent = readNumericValue(group.percentageChange);
        return {
          name: translateDynamicLabel(group.name) || group.name || t.unnamedList,
          members,
          membersLabel:
            readFormattedText(group.countFormattedValue) ?? formatLocalizedNumber(members),
          trend:
            changePercent === null
              ? undefined
              : { changePercent, needsAttention: groupNeedsAttention(group.name, changePercent) },
        };
      }),
    [groupItems, translateDynamicLabel, formatLocalizedNumber, t]
  );

  const narrativeData = useMemo(
    () => ({
      audience_overview: {
        total_members: totalMembers,
        active_lists: activeLists.length,
        archived_lists: data.length - activeLists.length,
        lists_by_type: kindCounts,
        lists: activeLists.map(list => ({
          name: list.list_name,
          type: getAudienceListKind(list.list_type),
          members: readNumericValue(list.members),
          tags: list.tags ?? [],
        })),
      },
      ...(achievements.data ? { achievements: achievements.data } : {}),
    }),
    [totalMembers, activeLists, data.length, kindCounts, achievements.data]
  );

  const {
    narrative,
    loading: narrativeLoading,
    aiSummary,
  } = useDashboardNarrative({
    dashboardKey: "engagement_overview",
    tabKey: "audience",
    startDate,
    endDate,
    data: narrativeData,
    enabled: !initialLoading,
  });

  const formatMembers = useCallback(
    (list: CustomList) => {
      const formatted =
        readFormattedText(list.membersFormattedValue) ?? readFormattedText(list.members);
      if (formatted) {
        return formatted;
      }
      const numericValue = readNumericValue(list.members) ?? Number(list.members);
      return Number.isFinite(numericValue) ? formatLocalizedNumber(numericValue) : EMPTY_VALUE;
    },
    [formatLocalizedNumber]
  );

  const handleScopeChange = useCallback((archived: boolean) => {
    setShowArchived(archived);
    setSelectedLists([]);
    setTagFilter("");
  }, []);

  const handleToggleAll = useCallback(
    (checked: boolean) => setSelectedLists(checked ? visibleIds : []),
    [visibleIds]
  );

  const handleToggleRow = useCallback((listId: number, checked: boolean) => {
    setSelectedLists(prev =>
      checked
        ? prev.includes(listId) ? prev : [...prev, listId]
        : prev.filter(id => id !== listId)
    );
  }, []);

  const handleToggleSort = useCallback(() => {
    setSortOrder(order => (order === "newest" ? "oldest" : "newest"));
  }, []);

  const handleOpenList = useCallback(
    (list: CustomList) => setOpenedListId(list.custom_list_id),
    []
  );

  const handleCloseList = useCallback(() => setOpenedListId(null), []);

  const handleListSaved = useCallback(() => {
    setOpenedListId(null);
    fetchCustomLists();
  }, [fetchCustomLists]);

  const openSingleActionModal = useCallback((list: CustomList, action: AudienceAction) => {
    setSelectedItem(list.list_name);
    setSelectedListId(list.custom_list_id);
    setActionType(action);
    setModalOpen(true);
  }, []);

  const handleArchiveToggle = useCallback(
    (list: CustomList) =>
      openSingleActionModal(list, isArchivedList(list) ? "unarchive" : "archive"),
    [openSingleActionModal]
  );

  const handleDeleteClick = useCallback(
    (list: CustomList) => openSingleActionModal(list, "delete"),
    [openSingleActionModal]
  );

  const handleExportRow = useCallback(
    (list: CustomList) => exportSingleList(list.custom_list_id),
    [exportSingleList]
  );

  const columns = useAudienceColumns({
    showArchived,
    selectedIds: selectedLists,
    allSelected,
    onToggleAll: handleToggleAll,
    onToggleRow: handleToggleRow,
    onToggleSort: handleToggleSort,
    onOpen: handleOpenList,
    onExport: handleExportRow,
    onDelete: handleDeleteClick,
    onArchiveToggle: handleArchiveToggle,
    translateLabel: translateDynamicLabel,
  });

  const handleBulkAction = (action: AudienceAction) => {
    if (selectedLists.length === 0) {
      message.warning(t.pleaseSelectAtLeastOneList);
      return;
    }
    setActionType(action);
    setSelectedItem(null);
    setSelectedListId(null);
    setModalOpen(true);
  };

  const handleExportClick = () => {
    if (selectedLists.length === 0) {
      message.warning(t.pleaseSelectAtLeastOneAudienceList);
      return;
    }
    if (selectedLists.length === 1) {
      exportSingleList(selectedLists[0]);
      return;
    }
    setExportModalOpen(true);
  };

  const handleExportCombined = async () => {
    await exportCombinedLists(selectedLists);
    setExportModalOpen(false);
  };

  const handleExportSeparate = async () => {
    await exportSeparateLists(selectedLists);
    setExportModalOpen(false);
  };

  const getActionLabel = (action: AudienceAction | null) => {
    if (action === "archive") {return t.archive;}
    if (action === "unarchive") {return t.unarchive;}
    if (action === "delete") {return t.delete;}
    return "";
  };

  const confirmStatusChange = async (listId: number, action: "archive" | "unarchive") => {
    try {
      const response = await updateCustomListStatus(
        listId,
        action === "unarchive" ? "active" : "archive"
      );
      if (response.status === "success") {
        message.success(
          (action === "archive" ? t.listArchivedSuccess : t.listUnarchivedSuccess)
            .replace("{{item}}", selectedItem || "")
        );
        await fetchCustomLists();
      } else {
        message.error(t.failedToActionList.replace("{{action}}", getActionLabel(action)));
      }
    } catch (err) {
      console.error(t.errorActioningList.replace("{{action}}", getActionLabel(action)), err);
      message.error(t.errorActioningList.replace("{{action}}", getActionLabel(action)));
    }
  };

  const confirmDelete = async (listId: number) => {
    try {
      const response = await deleteCustomList(listId);
      if (response.status === "success") {
        message.success(t.listDeletedSuccess.replace("{{item}}", selectedItem || ""));
        setData(prev => prev.filter(item => item.custom_list_id !== listId));
      } else {
        message.error(t.failedToDeleteListSegment);
      }
    } catch (err) {
      console.error(t.errorDeletingListSegment, err);
      message.error(t.errorDeletingListSegment);
    }
  };

  const resetActionState = () => {
    setActionLoading(false);
    setModalOpen(false);
    setSelectedItem(null);
    setSelectedListId(null);
    setSelectedLists([]);
    setActionType(null);
  };

  const handleModalConfirm = async () => {
    if (!actionType) {return;}
    setActionLoading(true);
    try {
      if (selectedListId === null) {
        await handleBulkActionConfirm(selectedLists, actionType, fetchCustomLists, setActionLoading);
      } else if (actionType === "delete") {
        await confirmDelete(selectedListId);
      } else {
        await confirmStatusChange(selectedListId, actionType);
      }
    } catch (error) {
      console.error(error);
    } finally {
      resetActionState();
    }
  };

  const handleModalCancel = () => {
    setModalOpen(false);
    setSelectedItem(null);
    setSelectedListId(null);
    setActionType(null);
    setActionLoading(false);
  };

  const modalTitle = actionType
    ? `${getActionLabel(actionType)} ${selectedItem ? t.listsSegmentSingular : t.listsSegmentPlural}`
    : "";

  const modalDescription = selectedItem
    ? t.areYouSureActionOne
        .replace("{{action}}", getActionLabel(actionType))
        .replace("{{item}}", selectedItem)
    : t.areYouSureActionMany
        .replace("{{action}}", getActionLabel(actionType))
        .replace("{{count}}", String(selectedLists.length));

  const loadingLabels: Record<AudienceAction, string> = {
    delete: t.deleting,
    archive: t.archiving,
    unarchive: t.unarchiving,
  };

  if (initialLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="campaign-dashboard audience-overview">
      <header className="audience-overview__header">
        <div className="audience-overview__heading">
          <h1 className="audience-overview__title">{t.audienceOverview}</h1>
          <p className="audience-overview__subtitle">{t.pageSubtitle}</p>
        </div>
        <AudienceCreateMenu />
      </header>

      <DashboardNarrativePanel
        narrative={narrative}
        loading={narrativeLoading}
        aiSummary={aiSummary}
        defaultCollapsed={false}
      />

      <div className="campaign-dashboard__achievements">
        <BusinessImpactSection
          metrics={businessImpact}
          subtitle={t.achievementsSubtitle}
          trendFractionDigits={2}
        />
      </div>

      <section className="campaign-performance-card audience-overview__table-card">
        <div className="campaign-card-header audience-overview__table-header">
          <div className="audience-overview__card-heading">
            <div className="audience-overview__card-title-row">
              <span className="campaign-card-title">
                {showArchived ? t.archivedAudience : t.activeAudience}
              </span>
              <DefinitionTooltip
                label={showArchived ? t.archivedAudience : t.activeAudience}
                info={{ description: t.audienceTableTooltip }}
              />
            </div>
            <p className="audience-overview__card-subtitle">{t.audienceTableSubtitle}</p>
          </div>
          <div className="campaign-card-actions">
            <button type="button" className="campaign-action-btn" onClick={handleExportClick}>
              <Download size={15} aria-hidden />
              {t.export}
            </button>
            <button
              type="button"
              className="campaign-action-btn"
              onClick={() => handleBulkAction(showArchived ? "unarchive" : "archive")}
            >
              <Archive size={15} aria-hidden />
              {showArchived ? t.unarchive : t.archive}
            </button>
            <button
              type="button"
              className="campaign-action-btn campaign-action-btn--danger"
              disabled={hasPredictedSelection}
              title={hasPredictedSelection ? t.predictedDeleteDisabled : undefined}
              onClick={() => handleBulkAction("delete")}
            >
              <Trash2 size={15} aria-hidden />
              {t.delete}
            </button>
          </div>
        </div>

        <div className="campaign-card-toolbar">
          <div className="campaign-toolbar-left">
            <div
              className="campaign-scope-tabs"
              role="tablist"
              aria-label={t.activeAudience}
            >
              <button
                type="button"
                role="tab"
                aria-selected={!showArchived}
                className={`campaign-scope-tab ${!showArchived ? "is-active" : ""}`}
                onClick={() => handleScopeChange(false)}
              >
                {t.tabActive}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={showArchived}
                className={`campaign-scope-tab ${showArchived ? "is-active" : ""}`}
                onClick={() => handleScopeChange(true)}
              >
                {t.tabArchive}
              </button>
            </div>
          </div>
          <div className="campaign-toolbar-right">
            <StyledInput
              placeholder={t.search}
              value={searchQuery}
              onChange={event => setSearchQuery(event.target.value)}
              className="campaign-search-input audience-overview__search"
            />
            <SelectMenu
              value={typeFilter}
              options={typeOptions}
              onChange={setTypeFilter}
              ariaLabel={t.typeFilter}
              className="campaign-dashboard__select campaign-dashboard__select--toolbar"
              dropdownClassName="campaign-dashboard__select-dropdown"
            />
            <SelectMenu
              value={tagFilter}
              options={tagOptions}
              onChange={setTagFilter}
              ariaLabel={t.tagsFilter}
              className="campaign-dashboard__select campaign-dashboard__select--toolbar"
              dropdownClassName="campaign-dashboard__select-dropdown"
            />
          </div>
        </div>

        <CampaignTable<CustomList>
          key={lang}
          className="audience-overview__table"
          columns={columns}
          data={paginatedLists}
          rowKey="custom_list_id"
          loading={listsLoading}
          locale={{
            emptyText: <NoDataDisplay message={t.noListsFound} altText={t.noDataAlt} />,
          }}
          customPagination={
            <TablePagePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPrevious={() => setCurrentPage(page => Math.max(1, page - 1))}
              onNext={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
            />
          }
        />
      </section>

      <AudienceBarChart data={predictedGroups} loading={customerGroups.loading} />

      {openedList && (
        <AudienceListModal
          key={openedList.custom_list_id}
          list={openedList}
          onClose={handleCloseList}
          onSaved={handleListSaved}
          onImported={fetchCustomLists}
          translateLabel={translateDynamicLabel}
          formatMembers={formatMembers}
        />
      )}

      <ConfirmationModal
        modalOpen={modalOpen}
        title={modalTitle}
        description={modalDescription}
        icon={actionType ? ACTION_ICONS[actionType] : ""}
        confirmText={
          actionType ? (actionLoading ? loadingLabels[actionType] : getActionLabel(actionType)) : ""
        }
        cancelText={t.cancel}
        confirmButtonProps={{ loading: actionLoading }}
        onConfirm={handleModalConfirm}
        onCancel={handleModalCancel}
      />
      <ExportConfirmationModal
        open={exportModalOpen}
        onCancel={() => setExportModalOpen(false)}
        onCombine={handleExportCombined}
        onSeparate={handleExportSeparate}
        selectedCount={selectedLists.length}
      />
    </div>
  );
};

export default AudienceScreen;